import { useEffect, useRef } from 'react';

import { RotateCcw, Sparkles } from 'lucide-react';

import { useWealthChat } from '../hooks/useWealthChat';
import { cn } from '../utils/cn';
import { ChatComposer } from './ChatComposer';
import { ChatMessageBubble } from './ChatMessageBubble';
import { SuggestedPrompts } from './SuggestedPrompts';

export const WealthChat = ({ className }: { className?: string }) => {
    const { messages, isStreaming, send, stop, reset } = useWealthChat();
    const scrollRef = useRef<HTMLDivElement>(null);
    const stickToBottomRef = useRef(true);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (el && stickToBottomRef.current) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const handleSend = (text: string) => {
        stickToBottomRef.current = true;
        void send(text);
    };

    const isFresh = messages.length <= 1;

    return (
        <section
            className={cn(
                'flex flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm',
                className,
            )}
        >
            <header className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                        <Sparkles className="h-4 w-4" />
                        <span
                            className={cn(
                                'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-slate-900',
                                isStreaming ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400',
                            )}
                        />
                    </div>
                    <div>
                        <div className="text-sm font-black tracking-tight text-slate-900 dark:text-white">
                            Education Assistant
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            {isStreaming ? 'Typing…' : 'Online · Private model'}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={reset}
                    disabled={isFresh && !isStreaming}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                    <RotateCcw className="h-3.5 w-3.5" /> New chat
                </button>
            </header>

            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 space-y-5 overflow-y-auto px-4 py-5"
                aria-live="polite"
            >
                {messages.map((m) => (
                    <ChatMessageBubble key={m.id} message={m} />
                ))}
                {isFresh && (
                    <div className="pt-2">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            Try asking
                        </div>
                        <SuggestedPrompts disabled={isStreaming} onPick={handleSend} />
                    </div>
                )}
            </div>

            <ChatComposer isStreaming={isStreaming} onSend={handleSend} onStop={stop} />
        </section>
    );
};

export default WealthChat;
